import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Hero from '../components/sections/Hero';
import FeaturesStrip from '../components/sections/FeaturesStrip';
import HowItWorks from '../components/sections/HowItWorks';
import FeaturedFleet from '../components/sections/FeaturedFleet';
import FeaturedDrivers from '../components/sections/FeaturedDrivers';
import Testimonials from '../components/sections/Testimonials';
import CallToAction from '../components/sections/CallToAction';
import Footer from '../components/layout/Footer';
import SeoHead from '../components/seo/SeoHead';
import { publicService } from '../services/public.service';

const LandingPage = () => {
  const [landingData, setLandingData] = useState({
    featuredCars: [],
    testimonials: []
  });
  const [driverTab, setDriverTab] = useState('featured');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLandingData = async () => {
      try {
        const [data, categoryData] = await Promise.all([
          publicService.getLandingPageData(),
          publicService.getCategories()
        ]);
        setLandingData(data);
        setCategories(categoryData || []);
      } catch (error) {
        console.error('Error fetching landing page data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLandingData();
  }, []);

  return (
    <div className="min-h-screen bg-[#faf8f5] flex flex-col font-sans">
      <SeoHead
        title="Go Rides | Premium Car Rental in Rwanda"
        description="Rent premium cars in Rwanda with flexible booking, trusted fleet owners, transparent pricing, and seamless support from Go Rides."
        canonicalPath="/"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Go Rides',
          url: window.location.origin,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${window.location.origin}/browse?search={search_term_string}`,
            'query-input': 'required name=search_term_string'
          }
        }}
      />
      <Navbar />
      <Hero />
      <FeaturesStrip />
      <FeaturedFleet cars={landingData.featuredCars} categories={categories} isLoading={loading} />
      <section className="py-8 sm:py-10 bg-[#faf8f5]"><div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange-500">The people behind the journey</p><div className="inline-flex rounded-full border border-gray-200 bg-white p-1" role="group" aria-label="Driver collection">{[['featured', 'Featured'], ['rated', 'Highest rated']].map(([value, label]) => <button key={value} aria-pressed={driverTab === value} onClick={() => setDriverTab(value)} className={`rounded-full px-4 py-2 text-sm font-semibold ${driverTab === value ? 'bg-brand-black text-white' : 'text-gray-500'}`}>{label}</button>)}</div></div>
        <FeaturedDrivers compact highestRated={driverTab === 'rated'} limit={3} compactCards />
      </div></section>
      <HowItWorks />
      <Testimonials testimonials={landingData.testimonials} isLoading={loading} />
      <section className="px-4 py-6 bg-[#faf8f5]"><div className="max-w-7xl mx-auto rounded-[2rem] bg-brand-black p-5 sm:p-7 flex flex-col md:flex-row md:items-center justify-between gap-5"><div className="max-w-xl"><p className="text-xs uppercase tracking-[0.2em] text-brand-orange-500 font-semibold">Grow with Go Rides</p><h2 className="text-2xl sm:text-3xl font-bold text-white mt-3">Your car. Your skills. More possibilities.</h2><p className="text-gray-400 mt-3">Join as a car owner or driver and connect with people planning their next journey.</p></div><div className="flex flex-col gap-3"><Link to="/register?role=CAR_OWNER" className="inline-flex justify-center gap-3 items-center rounded-full bg-brand-orange-500 hover:bg-brand-orange-600 text-white font-semibold px-6 py-3.5">Become our partner <ArrowUpRight className="h-5 w-5" /></Link><Link to="/driver" className="text-sm text-white text-center hover:text-brand-orange-500">Explore the driver portal →</Link></div></div></section>
      <CallToAction />
      <Footer compact />
    </div>
  );
};

export default LandingPage;
